import * as m from 'motion/react-m'
import { useScroll, useTransform } from 'motion/react'
import { Motion } from '@/lib/motion/lazy-motion'
import { cva } from 'class-variance-authority'
import Container from '@/components/structure/Container'
import EmailSignUp from '@/components/ui/EmailSignUp'
import { cn } from '@/lib/utils'
import type { App } from '@/payload-types'

const backgroundTileVariants = cva(
  'relative h-40 w-40 squircle border border-black/10 shadow-2xl',
  {
    variants: {
      variant: {
        default: 'z-10 bg-gradient-to-b from-white/50 to-transparent opacity-80',
        base: 'z-10 bg-gradient-to-b from-white to-white/50',
        middle:
          'z-20 bg-gradient-to-b from-white to-white/50 transition-transform duration-1000 ease-in-out group-hover:-translate-x-0 group-hover:-translate-y-0',
        top: 'z-30 transition-transform duration-1000 ease-in-out group-hover:-translate-x-0 group-hover:-translate-y-0',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

function BackgroundTile({
  variant = 'default',
  className,
  style,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { variant?: 'default' | 'base' | 'middle' | 'top' }) {
  return (
    <div className={cn(backgroundTileVariants({ variant }), className)} style={style} {...props} />
  )
}

type BlogHeroProps = {
  apps: App[]
  type?: 'standard' | 'editorial' | 'root'
  children?: React.ReactNode
}

export default function BlogHero({ apps, type = 'standard', children }: BlogHeroProps) {
  const height = 680
  const { scrollY } = useScroll()

  const scrollUp = useTransform(scrollY, [0, height], [0, -50])
  const opacity = useTransform(scrollY, [0, height], [1, 0])

  return (
    <div
      className={cn(
        'relative overflow-hidden',
        type === 'editorial' ? 'h-auto' : 'top-20 mb-20 h-auto py-10',
      )}
    >
      <Container
        reduced={true}
        height="fill"
        className={cn(
          'relative z-20 flex h-full w-full flex-col items-center justify-center gap-11 md:flex-row md:gap-10',
        )}
      >
        <Motion>
          <m.div style={{ opacity: opacity, y: scrollUp }} className="flex w-full flex-col gap-8">
            {type === 'standard' || type === 'editorial' ? children : <RootHero />}
          </m.div>
        </Motion>
      </Container>

      {type !== 'editorial' && <Tiles apps={apps} />}
    </div>
  )
}

export function RootHero() {
  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-dark-950 dark:text-white">Blog</h1>
      <p className="text-14 leading-normal text-gray-600/60 dark:text-white/60 md:text-18">
        Stories, insights, and app guides from Efficient App
      </p>
      <EmailSignUp focus="New Posts" maxWidth={480} />
    </div>
  )
}

export function Tiles({ apps: _apps }: { apps: App[] }) {
  // const hideApps = [2, 3, 6, 9]

  return (
    <div
      style={{
        WebkitMaskImage: 'radial-gradient(circle, rgb(0, 0, 0) 10%, rgba(0, 0, 0, 0) 75%)',
        maskImage: 'radial-gradient(circle, rgb(0, 0, 0) 10%, rgba(0, 0, 0, 0) 75%)',
      }}
      className="absolute -right-10 flex w-[705px] -translate-y-[600px] -rotate-[42deg] skew-x-[16deg] skew-y-12 flex-wrap gap-8 p-20 md:-translate-y-[640px]"
    >
      {Array.from({ length: 9 }).map((_, i) => (
        <BackgroundTile key={`bg-tile-${i}`}>
          {/* hideApps.includes(i) ? null : (
            <AppIcon icon={apps[i].icon} name={apps[i].name} size={160} />
          )*/}
        </BackgroundTile>
      ))}
    </div>
  )
}
